<template>
  <div>
    <v-row>
      <v-col cols="12">
        <h1 class="display-1 font-weight-bold mx-1 mb-3">Shopping Cart</h1>
        <v-card>
          <v-card-text>
            <v-data-table
              v-if="!loadingData"
              id="testHtml"
              :items="cart.items"
              item-key="orderNumber"
              :headers="headers"
              hide-default-footer
              :disable-pagination="true"
              no-data-text="No items in your cart"
            >
              <template v-slot:[`item.img`]="{ item }">
                <v-img
                  class="mx-2"
                  max-height="50px"
                  max-width="50px"
                  :src="`${imageUrl}/100px/${getImageToDisplay(
                    item.product.productImage
                  )}`"
                ></v-img>
              </template>
              <template v-slot:[`item.artwork.title`]="{ item }">
                {{ item.artwork.title }} by {{ item.artist.name }}
              </template>
              <template v-slot:[`item.product`]="{ item }">
                {{ item.productTypeVariant.title }}<br />
                {{
                  item.size ||
                  item.productTypeVariant.greetingCardSize ||
                  item.productTypeVariant.mountedPrintSize ||
                  item.productTypeVariant.magnetSize ||
                  ""
                }}
              </template>
              <template v-slot:[`item.product.unitPrice`]="{ item }">
                £{{ item.unitPrice.toFixed(2) }}
              </template>
              <template v-slot:[`item.unitDiscount`]="{ item }">
                {{ item.unitDiscount && item.unitDiscount + "%" }}
              </template>
              <template v-slot:[`item.qty`]="{ item }">
                <div class="d-flex align-center mr-2">
                  <v-icon small @click.native="decrement(item)">
                    mdi-chevron-left
                  </v-icon>
                  <span class="mx-2">{{ item.qty }}</span>
                  <v-icon small @click.native="increment(item)">
                    mdi-chevron-right
                  </v-icon>
                </div>
              </template>
              <template v-slot:[`item.total`]="{ item }">
                £{{
                  (item.unitDiscount
                    ? (item.unitPrice * item.qty * (100 - item.unitDiscount)) / 100
                    : item.unitPrice * item.qty
                  ).toFixed(2)
                }}
              </template>
              <template v-slot:[`item.remove`]="{ item }">
                <v-icon @click.native="remove(item)"> mdi-delete </v-icon>
              </template>
              <template slot="body.append">
                <tr>
                  <th style="font-size: 0.875rem; text-align: left">Total (Net)</th>
                  <th colspan="5"></th>
                  <th style="font-size: 0.875rem; text-align: right">£{{ total }}</th>
                  <th />
                </tr>
              </template>
            </v-data-table>
            <v-alert
              v-if="!validMugs"
              class="mt-2"
              width="50%"
              border="top"
              colored-border
              type="error"
              elevation="2"
            >
              <p>
                For courier deliveries mugs must be ordered in multiples of 36. This can
                be any mixture of designs, with each design in multiples of 3
              </p>
              <p>Currently your cart contains {{ totalMugs }} mugs</p>
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card class="fill-height">
          <v-card-title>Delivery</v-card-title>
          <v-card-text class="pb-0">
            <v-row>
              <v-col sm="4">
                <v-select
                  outlined
                  v-model="cart.delivery"
                  :items="deliveryOptions"
                  @change="updateFields"
                  label="Delivery Type"
                  hide-details
                >
                </v-select>
              </v-col>
            </v-row>
            <v-radio-group
              v-if="hasShippingAddress && cart.delivery === 'COURIER'"
              v-model="cart.shipTo"
              @change="updateFields"
              mandatory
              row
            >
              <v-row class="address">
                <v-col md="6">
                  <v-card class="fill-height">
                    <v-card-title>
                      <v-radio label="Use Billing Address" value="BILLING"></v-radio>
                    </v-card-title>
                    <v-card-text class="address" v-html="formatAddress(user.address)" />
                  </v-card>
                </v-col>
                <v-col md="6">
                  <v-card class="fill-height">
                    <v-card-title>
                      <v-radio label="Use Shipping Address" value="SHIPPING"></v-radio>
                    </v-card-title>
                    <v-card-text
                      class="address"
                      v-html="
                        formatAddress(
                          hasShippingAddress ? user.shippingAddress : user.address
                        )
                      "
                    />
                  </v-card>
                </v-col>
              </v-row>
            </v-radio-group>
            <v-row
              class="address"
              v-if="!hasShippingAddress && cart.delivery === 'COURIER'"
            >
              <v-col md="6">
                <v-card class="fill-height">
                  <v-card-title> Ship to </v-card-title>
                  <v-card-text class="address" v-html="formatAddress(user.address)" />
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-text v-if="cart.delivery === 'COURIER'" class="py-0 mt-4">
            <ul>
              <li><strong>UK Mainland Deliveries</strong> £8.00 + VAT (up to 20Kg)</li>
              <li><strong>Islands</strong> £22.30 + VAT (up to 20Kg)</li>
            </ul>
            <p class="my-2">
              In our experience, approximately 90% of all customer orders are under 20kg.
            </p>
            <p class="mt-2">
              If your order is above 20kg, your basic shipping cost outlined above will be
              subject to £0.37 additional per kg surcharge.
            </p>
          </v-card-text>
          <v-card-text v-if="cart.delivery === 'COLLECT'" class="pb-0">
            <p>We will contact you when your order is ready to be collected.</p>
            <p>
              Our opening hours are 7.30am - 3.30pm. If you need to collect outwith these
              hours please get in touch with us on 0131 553 2772 or email us at
              sales@doricmor.com
            </p>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col sm="12" lg="6">
        <v-card class="fill-height">
          <v-card-title>If you have a promotional code enter it here</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="4">
                <v-text-field
                  v-model="promoCode"
                  label="Promo Code"
                  dense
                  hide-details
                  outlined
                  clearable
                  @input="promoCode = promoCode?.toUpperCase()"
                ></v-text-field>
                <v-btn
                  class="mt-2"
                  color="success"
                  block
                  :disabled="!promoCode"
                  @click.native="applyPromotion()"
                >
                  Apply Promotion
                </v-btn>
              </v-col>
              <v-col>
                <v-alert
                  v-model="showPromoResult"
                  :icon="false"
                  outlined
                  dense
                  dismissible
                  :type="promoInfo.type"
                >
                  {{ promoInfo.message }}
                </v-alert>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col sm="12" lg="6">
        <v-card class="fill-height">
          <v-card-title>Additional Info</v-card-title>
          <v-card-text>
            <v-textarea
              v-model="cart.additionalInfo"
              label="Additional Info"
              auto-grow
              dense
              hide-details
              @change="updateFields"
              outlined
              clearable
            ></v-textarea>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col class="d-flex ml-auto" cols="12">
        <v-btn
          :disabled="isDirty || !cart.items.length"
          color="success"
          class="mr-4"
          @click.native="generatePdf()"
        >
          PDF
          <v-icon> mdi-download </v-icon>
        </v-btn>
        <v-btn
          :disabled="isDirty || !cart.items.length || !validMugs"
          color="success"
          class="mr-4"
          @click="submit"
          >Submit Order</v-btn
        >
        <v-btn :disabled="!isDirty" color="success" class="mr-4" @click="update"
          >Save Changes</v-btn
        >
        <v-btn :disabled="!isDirty || !cart.items.length" color="primary" @click="reset"
          >Reset</v-btn
        >
      </v-col>
    </v-row>
    <confirm-dialog ref="confirmDialog" />
  </div>
</template>

<script>
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { mapGetters } from "vuex";
import CartApi from "@/api/customer/cart";
import DiscountApi from "@/api/customer/discount";
import formatAddress from "@/utilities/formatAddress";
export default {
  components: {
    ConfirmDialog,
  },
  data() {
    return {
      loadingData: true,
      imageUrl: process.env.VUE_APP_IMG_URL,
      originalOrder: {},
      originalCart: {},
      isDirty: false,
      promoCode: null,
      showPromoResult: false,
      shipTo: "BILLING",
      promoInfo: {
        type: "success",
        message: "Discounts applied!",
      },
      deliveryOptions: [
        { text: "Courier", value: "COURIER" },
        { text: "Will collect", value: "COLLECT" },
      ],
      headers: [
        { text: "", value: "img" },
        {
          text: "Title",
          align: "start",
          value: "artwork.title",
        },
        { text: "Product", value: "product" },
        { text: "Unit Price", value: "product.unitPrice", align: "end" },
        { text: "Quantity", value: "qty" },
        { text: "Discount", value: "unitDiscount", align: "end" },
        { text: "Total", value: "total", align: "end" },
        { text: "", value: "remove" },
      ],
    };
  },
  methods: {
    reset() {
      // this.cart = { ...this.originalCart }
      this.$store.dispatch("cart/updateCart", this.originalCart);
      this.isDirty = false;
    },
    async applyPromotion() {
      DiscountApi.getDiscount(this.promoCode)
        .then(async (promo) => {
          await this.$store.dispatch("cart/applyPromotion", promo.data.discount);
          await CartApi.updateCart(this.$store.getters["cart/cart"]);
          this.showPromoResult = true;
          this.promoInfo.show = true;
          this.promoInfo.type = "success";
          this.promoInfo.message = `Discounts applied`;
        })
        .catch(async (err) => {
          console.log("err", err.response?.data, err);
          await this.$store.dispatch("cart/clearPromotion");
          this.showPromoResult = true;
          this.promoInfo.show = true;
          this.promoInfo.type = "warning";
          this.promoInfo.message = `Promo code ${this.promoCode} is invalid`;
          this.promoCode = null;
        });
      this.isDirty = false;
    },
    async update() {
      await this.$store.dispatch("cart/updateCart", this.cart);
      this.originalCart = JSON.parse(JSON.stringify(this.cart));
      await CartApi.updateCart(this.$store.getters["cart/cart"]);
      this.isDirty = false;
    },
    async submit() {
      if (
        await this.$refs.confirmDialog.open(
          "Submit this order?",
          "Once submitted you won't be able to modify the order. We will verify it and send you an email when we start producing it"
        )
      ) {
        this.$store.dispatch("cart/submitCart", this.cart);
      }
    },
    updateFields() {
      this.$store.dispatch("cart/updateCart", this.cart);
      this.isDirty = true;
    },
    increment(item) {
      const multQty = item.productTypeVariant.multQty || item.productType.multQty;
      item.qty += multQty;
      this.$store.dispatch("cart/updateCart", this.cart);
      this.isDirty = true;
    },
    decrement(item) {
      const multQty = item.productTypeVariant.multQty || item.productType.multQty;
      const minQty = item.productTypeVariant.minQty || item.productType.minQty;
      if (item.qty > minQty) {
        item.qty -= multQty;
        this.$store.dispatch("cart/updateCart", this.cart);
        this.isDirty = true;
      }
    },
    async remove(item) {
      if (
        await this.$refs.confirmDialog.open(
          "Confirm",
          "Are you sure you want to delete this item?"
        )
      ) {
        this.cart.items.splice(
          this.cart.items.findIndex((cartItem) => item.sku === cartItem.sku),
          1
        );
        this.$store.dispatch("cart/updateCart", this.cart);
        this.isDirty = true;
      }
    },
    async generatePdf() {
      CartApi.getCartPDF();
    },
    formatAddress(address) {
      let str = "";
      if (address.addressLine1) {
        str += `<p>${address.addressLine1}</p>`;
      }
      if (address.addressLine2) {
        str += `<p>${address.addressLine2}</p>`;
      }
      if (address.city) {
        str += `<p>${address.city}</p>`;
      }
      if (address.region) {
        str += `<p>${address.region}</p>`;
      }
      if (address.postalCode) {
        str += `<p>${address.postalCode}</p>`;
      }
      return str;
    },
  },
  computed: {
    ...mapGetters({
      cart: "cart/cart",
      validMugs: "cart/validMugs",
      totalMugs: "cart/totalMugs",
      total: "cart/total",
      user: "user",
      hasShippingAddress: "hasShippingAddress",
    }),
  },
  async created() {
    this.$store.dispatch("cart/getCart");
    this.originalCart = JSON.parse(JSON.stringify(this.cart));
    this.promoCode = this.cart.promo ? this.cart.promo.code : null;
    this.loadingData = false;
  },
  async beforeRouteLeave(to, from, next) {
    if (this.isDirty) {
      if (
        await this.$refs.confirmDialog.open(
          "Confirm",
          'Are you sure you want to leave this page?" You have unsaved changes'
        )
      ) {
        next();
      } else {
        next(false);
      }
    } else next();
  },
};
</script>
<style lang="scss">
.address {
  margin-bottom: 4px !important;
  font-weight: bold;
}
</style>
